import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  createQuestion,
  updateQuestion,
  deleteQuestion,
  getQuestion,
  getAllQuestions,
} from "../../services/getAdminApi";
import Swal from "sweetalert2";

const Question = () => {
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({
    question_text: "",
    options: [
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
    ],
  });
  const [editId, setEditId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      await getAllQuestions().then((response) => {
        if (response.status === 200) {
          if (response.data.length === 0) {
            setErrorMessage("No questions found.");
          } else {
            setQuestions(response.data);
          }
        }
      })

    } catch (error) {
      setErrorMessage("An error occurred while fetching questions.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOptionChange = (index, key, value) => {
    const updatedOptions = formData.options.map((option, i) => {
      if (key === "is_correct" && i !== index) {
        return { ...option, is_correct: false };
      }
      if (i === index) {
        return { ...option, [key]: key === "is_correct" ? value : value };
      }
      return option;
    });

    setFormData({ ...formData, options: updatedOptions });
  };

  const validateForm = () => {
    const correctCount = formData.options.filter((option) => option.is_correct).length;
    if (correctCount !== 1) {
      showAlert("Exactly one option must be marked as correct.", "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = {
        question_text: formData.question_text.trim(),
        options: formData.options.map((option) => ({
          option_text: option.option_text.trim(),
          is_correct: !!option.is_correct,
        })),
      };

      if (editId) {
        await updateQuestion(editId, payload);
        showAlert("Question updated successfully", "success");
      } else {
        await createQuestion(payload).then((res) => {
          if (res.status === 201) {
            showAlert("Question created successfully", "success");
          }
        })

      }
      fetchQuestions();
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to submit the question";
      const errorDetails = error.response?.data?.errors || {};
      const detailedMessage =
        Object.values(errorDetails).flat().join("\n") || errorMessage;
      showAlert(detailedMessage, "error");
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await getQuestion(id);
      setFormData(response.data);
      setEditId(id);
      setOpenDialog(true);
    } catch {
      showAlert("Failed to fetch question details", "error");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
    });

    if (result.isConfirmed) {
      try {
        await deleteQuestion(id);
        showAlert("Question deleted successfully", "success");
        fetchQuestions();
      } catch {
        showAlert("Failed to delete the question", "error");
      }
    }
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setEditId(null);
    setFormData({
      question_text: "",
      options: [
        { option_text: "", is_correct: false },
        { option_text: "", is_correct: false },
        { option_text: "", is_correct: false },
        { option_text: "", is_correct: false },
      ],
    });
  };

  const showAlert = (message, severity) => {
    Swal.fire({
      icon: severity,
      title: severity === "error" ? "Error" : "Success",
      text: message,
    });
  };

  return (
    <div style={{ padding: "15px"}}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={() => setOpenDialog(true)}
        style={{ marginBottom: "20px" }}
      >
        Create Question
      </Button>

      {loading ? (
        <p>Loading...</p>
      ) : errorMessage ? (
        <p style={{ color: "red" }}>{errorMessage}</p>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Question</TableCell>
                <TableCell>Options</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {questions.map((question) => (
                <TableRow key={question.id}>
                  <TableCell>{question.question_text}</TableCell>
                  <TableCell>
                    {question.options.map((option, index) => (
                      <div key={index}>
                        {option.option_text} {option.is_correct && "(Correct)"}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleEdit(question.id)}>
                      <Edit />
                    </IconButton>
                    <IconButton color="secondary" onClick={() => handleDelete(question.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? "Edit Question" : "Create Question"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Question Text"
            name="question_text"
            value={formData.question_text}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          {formData.options.map((option, index) => (
            <div key={index} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <TextField
                label={`Option ${index + 1}`}
                value={option.option_text}
                onChange={(e) => handleOptionChange(index, "option_text", e.target.value)}
                required
              />
              <Button
                variant={option.is_correct ? "contained" : "outlined"}
                color="primary"
                onClick={() => handleOptionChange(index, "is_correct", true)}
              >
                {option.is_correct ? "Correct" : "Mark Correct"}
              </Button>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Question;
